import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BKFreelancerPayOutDataPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookKeeperView, setIsBookKeeperView] = useState(false);

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'Transaction ID',
    'Freelancer ID',
    'Freelancer Name',
    'Transaction Type',
    'Payout Amount',
    'Order ID',
    'Remarks',
    'Transaction Date',
    'Cashfree Transfer ID' // ✅ Added
  ];

  const bookKeeperColumns = [
    'Transaction ID',
    'Freelancer ID',
    'Freelancer Name',
    'Payout Amount',
    'Transaction Date',
    'Cashfree Transfer ID' // ✅ Added
  ];

  useEffect(() => {
    console.log('📦 Component mounted — BKFreelancerPayOutDataPage');

    const fetchTransactions = async () => {
      console.log('🔄 Initiating fetch for payout transactions...');
      const token = localStorage.getItem('employeeToken');
      console.log('📥 Retrieved token:', token);

      if (!token) {
        console.warn("🚫 No employee token found — redirecting to login");
        window.location.href = "/employee-login";
        return;
      }

      try {
        console.log(`🌐 Sending GET request to ${apiBaseURL}/api/bk-freelancer-payout-data`);
        const response = await axios.get(`${apiBaseURL}/api/bk-freelancer-payout-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('✅ API response received:', response.data);
        setTransactions(response.data);
      } catch (err) {
        console.error('❌ Error during payout transactions fetch:', err);
        setError('Failed to fetch payout transactions.');
      } finally {
        setLoading(false);
        console.log('✅ Fetch process completed');
      }
    };

    fetchTransactions();
  }, [apiBaseURL]);

  if (loading) return <div>Loading payout transactions...</div>;
  if (error) return <div>{error}</div>;

  const processData = (data) =>
    data.map((transaction) => {
      const amount = Number(transaction.amount);
      const transactionDate = transaction.transaction_date
        ? new Date(transaction.transaction_date).toLocaleString()
        : 'N/A';

      return {
        transaction_id: transaction.transaction_id,
        freelancer_id: transaction.freelancer_id,
        freelancer_name: transaction.name || 'N/A',
        transaction_type: transaction.transaction_type,
        payout_amount: isNaN(amount) ? '0.00' : amount.toFixed(2),
        order_id: transaction.order_id || 'N/A',
        remarks: transaction.remarks || 'N/A',
        transaction_date: transactionDate,
        cashfree_transfer_id: transaction.cashfree_transfer_id || '—' // ✅ Added
      };
    });

  const data = processData(transactions);

  const filteredData = isBookKeeperView
    ? data.filter((t) => t.transaction_type === 'Withdrawal')
    : data;

  const activeColumns = isBookKeeperView ? bookKeeperColumns : columns;

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, idx) => (
              <th key={idx} className="px-3 py-2 text-left">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-3 text-center text-gray-500">
                No payout transaction data found.
              </td>
            </tr>
          ) : (
            data.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b">
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 whitespace-nowrap">
                    {row[col.toLowerCase().replace(/ /g, '_')]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const handleDownload = () => {
    const now = new Date();
    const fileName = `Freelancer_Payout_Data_${now.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;

    const headerRows = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Payout Data'],
      [`Downloaded on: ${now.toLocaleString()}`],
      [],
    ];

    const tableHeaders = activeColumns;
    const tableData = filteredData.map((item) =>
      activeColumns.map((col) => item[col.toLowerCase().replace(/ /g, '_')])
    );

    const wsData = [...headerRows, tableHeaders, ...tableData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PayoutData');

    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="flex-1 p-6 bg-gray-50 ml-64">
      <h2 className="text-2xl font-semibold mb-5">
        Freelancer Payout Data (fetched from freelance_payout_transactions and freelancer_basic_info)
      </h2>

      <div className="flex gap-4 mb-6">
        <button
          onClick={handleDownload}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
        >
          Download as Excel
        </button>

        <button
          onClick={() => setIsBookKeeperView(!isBookKeeperView)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          {isBookKeeperView ? 'Show All Data' : 'Show Data for Book-Keeper'}
        </button>
      </div>

      {isBookKeeperView && (
        <div className="mb-4 text-lg font-medium">
          Total Payout Amount: ₹{' '}
          {filteredData
            .reduce((acc, curr) => acc + parseFloat(curr.payout_amount || 0), 0)
            .toFixed(2)}
        </div>
      )}

      <Table columns={activeColumns} data={filteredData} />
    </div>
  );
};

export default BKFreelancerPayOutDataPage;
